import { ReactElement, createContext, useEffect, useState, useRef } from 'react';
import Notification from '@/components/news/Notification';
import { AlertColor } from '@mui/material';

type NotificationMessage = {
  severity: AlertColor;
  message: string;
  onClose?: () => void;
  timeoutSec?: number | undefined;
};

export type NotificationContextType = {
  showNotification: (severity: AlertColor, message: string, onClose?: () => void, timeoutSec?: number) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ctxNotification = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactElement }) {
  const [notificationMessages, setNotificationMessages] = useState<NotificationMessage[]>([]);
  const notificationsAllowed = useRef<boolean>(true);
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    window.allowNotifications = function (allowed) {
      notificationsAllowed.current = allowed;
    };
    return () => {
      window.allowNotifications = () => {};
    };
  }, []);

  function startTimer(current: NotificationMessage) {
    if (current.timeoutSec) {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(hideNotification, current.timeoutSec * 1000);
    }
  }

  function hideNotification() {
    setNotificationMessages((prev) => {
      if (prev.length) {
        // ha az user kezzel kapcsolja ki a notification-t de a timer maradna
        // akkor a timer a queue-ban kovetkezot kapcsolna ki
        if (timer.current) {
          clearTimeout(timer.current);
          timer.current = undefined;
        }
        prev[0].onClose?.();
        prev.shift();
        if (prev.length) startTimer(prev[0]);
      }
      return [...prev];
    });
  }

  function isDuplicate(current: NotificationMessage[], newNotificationMessage: NotificationMessage) {
    return current.find((value) => value.message === newNotificationMessage.message) !== undefined;
  }

  function showNotification(severity: AlertColor, message: string, onClose?: () => void, timeoutSec?: number) {
    if (!notificationsAllowed.current) {
      return;
    }

    const newNotificationMessage = {
      severity,
      message,
      onClose,
      timeoutSec,
    } satisfies NotificationMessage;

    setNotificationMessages((current) => {
      if (isDuplicate(current, newNotificationMessage)) return current;

      if (!current.length) {
        startTimer(newNotificationMessage);
        return [newNotificationMessage];
      } else {
        return [...current, newNotificationMessage];
      }
    });
  }

  return (
    <ctxNotification.Provider value={{ showNotification }}>
      <div>
        {notificationMessages.length ? (
          <Notification
            key={notificationMessages[0].message}
            message={notificationMessages[0].message}
            close={hideNotification}
            severity={notificationMessages[0].severity}
          />
        ) : null}
      </div>
      {children}
    </ctxNotification.Provider>
  );
}
